import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import type { FieldValueConverter } from './dynamic-form.interfaces';

export interface OdinControllerProps<TValue, TFieldValues extends FieldValues = FieldValues>
  extends Omit<ControllerProps<TFieldValues>, 'name' | 'defaultValue'> {
  name: string;
  defaultValue?: unknown;
  onChange?: (value: TValue) => void;
  fieldValueConverter?: FieldValueConverter;
}

/**
 * Combines React Hook Form's `Controller` change handling with dynamic-form `onChange`.
 *
 * The value emitted by the wrapped field is passed to dynamic-form first, then forwarded to
 * React Hook Form (optionally transformed by `fieldValueConverter`).
 *
 * @typeParam TValue - Value shape emitted by the wrapped field component.
 * @typeParam TFieldValues - React Hook Form values shape used by the control.
 */
const OdinController = <TValue, TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  defaultValue,
  render,
  onChange,
  fieldValueConverter,
}: OdinControllerProps<TValue, TFieldValues>): React.JSX.Element => {
  // Can not apply undefined to defaultValue (https://legacy.react-hook-form.com/api/usecontroller/controller)
  const _defaultValue = defaultValue === undefined ? null : defaultValue;

  return (
    <Controller
      control={control}
      name={name as FieldPath<TFieldValues>}
      defaultValue={_defaultValue as ControllerProps<TFieldValues>['defaultValue']}
      render={({ field, fieldState, formState }) => {
        const { onChange: hfOnChange, ...rest } = field;

        const finalOnChange = (value: TValue) => {
          // dynamic form's onChange function already uses the field value converter
          onChange?.(value);

          // Calling onChange with undefined is not valid (https://legacy.react-hook-form.com/api/usecontroller/controller)
          const _value = value === undefined ? null : value;
          hfOnChange(fieldValueConverter ? fieldValueConverter(_value) : _value);
        };

        const newField = {
          ...rest,
          onChange: finalOnChange,
        };

        return render({ field: newField, fieldState, formState });
      }}
    />
  );
};

export default OdinController;
